import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessagesHeader } from './components/MessagesHeader';
import { FilterBar } from './components/FilterBar';
import { MessageList } from './components/MessageList';
import { MessageDetail } from './components/MessageDetail';
import { useMessages } from './hooks/useMessages';
import { useMessageFilters } from './hooks/useMessageFilters';
import { useRealtimeMessages } from './hooks/useRealtimeMessages';
import type { Message } from './types';

export const MessagesPage: React.FC = () => {
    const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);

    const {
        messages,
        stats,
        isLoading,
        error,
        refetch,
        markAsRead,
        markAllAsRead,
        archiveMessage,
        isMarkingAllAsRead,
    } = useMessages();

    const {
        filters,
        setFilterType,
        setSearch,
        filteredMessages,
        hasActiveFilters,
        clearFilters,
    } = useMessageFilters(messages);

    useRealtimeMessages();

    const handleSelectMessage = useCallback((message: Message) => {
        setSelectedMessage(message);
    }, []);

    const handleCloseDetail = useCallback(() => {
        setSelectedMessage(null);
    }, []);

    const handleMarkAsReadAndClose = useCallback(() => {
        if (selectedMessage) {
            markAsRead(selectedMessage.id);
        }
        handleCloseDetail();
    }, [selectedMessage, markAsRead, handleCloseDetail]);

    const handleArchiveAndClose = useCallback(() => {
        if (selectedMessage) {
            archiveMessage(selectedMessage.id);
        }
        handleCloseDetail();
    }, [selectedMessage, archiveMessage, handleCloseDetail]);

    const handleSelectWithClearFilters = useCallback((message: Message) => {
        if (hasActiveFilters) {
            clearFilters();
        }
        handleSelectMessage(message);
    }, [hasActiveFilters, clearFilters, handleSelectMessage]);

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[50vh] px-[18px]">
                <div className="w-12 h-12 rounded-xl bg-red-100 dark:bg-red-900/20 flex items-center justify-center text-red-500 mb-4">
                    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="12" y1="8" x2="12" y2="12"/>
                        <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    Ошибка загрузки
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 text-center">
                    Не удалось загрузить уведомления. Попробуйте обновить страницу.
                </p>
                <button
                    onClick={() => refetch()}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                >
                    Попробовать снова
                </button>
            </div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="pb-8 min-h-screen"
        >
            <MessagesHeader
                stats={stats}
                onMarkAllAsRead={markAllAsRead}
                onRefresh={refetch}
                isMarkingAllAsRead={isMarkingAllAsRead}
                isRefreshing={isLoading}
            />

            {hasActiveFilters && (
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="px-[18px] py-2 bg-amber-50 dark:bg-amber-900/10 border-y border-amber-200 dark:border-amber-500/20"
                >
                    <div className="flex items-center justify-between">
                        <span className="text-[12px] text-amber-700 dark:text-amber-400">
                            Активны фильтры: {filters.type !== 'all' && `тип "${filters.type}"`} 
                            {filters.search && `поиск "${filters.search}"`}
                        </span>
                        <button
                            onClick={clearFilters}
                            className="text-[12px] text-amber-700 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-300 font-medium underline"
                        >
                            Сбросить
                        </button>
                    </div>
                </motion.div>
            )}

            <FilterBar
                activeFilter={filters.type}
                onFilterChange={setFilterType}
                search={filters.search}
                onSearchChange={setSearch}
                stats={stats}
            />

            <div className="flex flex-col h-[calc(100vh-200px)]">
                <MessageList
                    messages={filteredMessages}
                    selectedId={selectedMessage?.id}
                    onSelect={handleSelectWithClearFilters}
                    onMarkAsRead={markAsRead}
                />
            </div>

            <AnimatePresence>
                {selectedMessage && (
                    <MessageDetail
                        message={selectedMessage}
                        onClose={handleCloseDetail}
                        onMarkAsRead={handleMarkAsReadAndClose}
                        onArchive={handleArchiveAndClose}
                    />
                )}
            </AnimatePresence>

            <div className="fixed bottom-4 right-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700 text-[11px] text-gray-500 dark:text-gray-400">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Обновления в реальном времени
            </div>
        </motion.div>
    );
};